import { useEffect, useState, useCallback } from "react";

/*
  /admin/beta-applications — Admin-only view (hidden from nav).
  Uses the existing JWT auth cookie. If the current user is not on
  BETA_ADMIN_EMAILS the backend returns 404 (opaque) — we surface
  that as an access-denied screen without confirming the resource
  even exists.
*/

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_LABELS = { AU: "AU · Paid Private Beta", UK: "UK · Early Access" };
const STATUS_TONES = {
  new:        { bg: "#EEF2F5", fg: "#1C1C1C" },
  reviewing:  { bg: "#FBEED9", fg: "#7A5A18" },
  contacted:  { bg: "#E8EFE7", fg: "#3C5A32" },
  accepted:   { bg: "#DDECDF", fg: "#2F5D3A" },
  declined:   { bg: "#F4DDD5", fg: "#7B3826" },
};
const STATUSES = ["new", "reviewing", "contacted", "accepted", "declined"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
    });
  } catch { return iso; }
};
const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const volumeLabel = (v) => (v || "").replace("_plus", "+").replace("_", "–");

export default function AdminBetaApplicationsPage() {
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [rows, setRows] = useState([]);
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true); setError("");
    const params = new URLSearchParams();
    if (country) params.set("country", country);
    if (status)  params.set("status", status);
    if (q)       params.set("q", q);
    const res = await fetch(`${API}/api/admin/beta-applications?${params}`, {
      credentials: "include",
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (res.status === 404) { setDenied(true); setLoading(false); return; }
    if (!res.ok) { setError("Could not load applications."); setLoading(false); return; }
    const data = await res.json();
    setRows(data);
    setLoading(false);
  }, [country, status, q]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openDetail = async (id) => {
    const res = await fetch(`${API}/api/admin/beta-applications/${id}`, {
      credentials: "include",
    });
    if (res.ok) setSelected(await res.json());
  };

  const patch = async (id, body) => {
    const res = await fetch(`${API}/api/admin/beta-applications/${id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const updated = await res.json();
      setSelected(updated);
      setRows(r => r.map(x => x.id === id ? updated : x));
    }
  };

  if (denied) {
    return (
      <div data-testid="admin-beta-denied"
           style={{ minHeight: "100vh", display: "flex", alignItems: "center",
                    justifyContent: "center", background: "#F9F8F6",
                    fontFamily: "Manrope, system-ui, sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ letterSpacing: "0.24em", textTransform: "uppercase",
                        color: "#8A8A8A", fontSize: 11, marginBottom: 8 }}>404</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, margin: 0 }}>
            Page not found
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-beta-page"
         style={{ minHeight: "100vh", background: "#F9F8F6",
                  fontFamily: "Manrope, system-ui, sans-serif", color: "#1C1C1C" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "36px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 28 }}>
          <div>
            <div style={{ letterSpacing: "0.24em", textTransform: "uppercase",
                          color: "#A68A64", fontSize: 11, fontWeight: 600 }}>
              Subtend Studio · Admin
            </div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif",
                         fontSize: 34, margin: "6px 0 0", fontWeight: 500 }}>
              Beta applications
            </h1>
          </div>
          <button onClick={fetchRows} data-testid="admin-beta-refresh"
                  style={{ background: "#1C1C1C", color: "white",
                           border: "none", padding: "10px 20px",
                           fontSize: 13, letterSpacing: "0.06em", cursor: "pointer" }}>
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div style={{ background: "white", padding: 16, marginBottom: 20,
                      display: "flex", gap: 12, flexWrap: "wrap",
                      border: "1px solid #E2DFD8" }}>
          <select value={country} onChange={e => setCountry(e.target.value)}
                  data-testid="admin-beta-filter-country"
                  style={{ padding: "10px 12px", border: "1px solid #D1CEC7",
                           background: "#FBFAF3", fontSize: 13.5 }}>
            <option value="">All countries</option>
            <option value="AU">AU</option>
            <option value="UK">UK</option>
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)}
                  data-testid="admin-beta-filter-status"
                  style={{ padding: "10px 12px", border: "1px solid #D1CEC7",
                           background: "#FBFAF3", fontSize: 13.5 }}>
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)}
                 data-testid="admin-beta-filter-q"
                 placeholder="Search name, email, region"
                 style={{ padding: "10px 12px", border: "1px solid #D1CEC7",
                          background: "#FBFAF3", fontSize: 13.5, flex: "1 1 240px" }}/>
        </div>

        {loading && (
          <div data-testid="admin-beta-loading" style={{ padding: 40, textAlign: "center", color: "#5C5C5C" }}>
            Loading…
          </div>
        )}
        {error && (
          <div data-testid="admin-beta-error"
               style={{ background: "#FBEEE8", color: "#8E3B26",
                        padding: "12px 14px", border: "1px solid #E8CBC0", marginBottom: 12 }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <div style={{ overflow: "auto", background: "white", border: "1px solid #E2DFD8" }}
               data-testid="admin-beta-table-wrap">
            <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: "#F4F2EC", textAlign: "left" }}>
                  {["Studio Market","Business Type","Annual Range","Contact","Email","Phone","Applied","Status",""].map(h => (
                    <th key={h} style={{ padding: "12px 14px", fontWeight: 600,
                                           letterSpacing: "0.08em", textTransform: "uppercase",
                                           fontSize: 11.5, color: "#5C5C5C",
                                           borderBottom: "1px solid #E2DFD8" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: 40, textAlign: "center", color: "#8A8A8A" }}>
                      No applications match your filters.
                    </td>
                  </tr>
                )}
                {rows.map(r => {
                  const tone = STATUS_TONES[r.status] || STATUS_TONES.new;
                  return (
                    <tr key={r.id} data-testid={`admin-beta-row-${r.id}`}
                        style={{ borderBottom: "1px solid #EDE9DF" }}>
                      <td style={{ padding: "14px" }}>{COUNTRY_LABELS[r.country] || r.country}</td>
                      <td style={{ padding: "14px" }}>{humanize(r.business_type)}</td>
                      <td style={{ padding: "14px" }}>{volumeLabel(r.annual_project_volume)}</td>
                      <td style={{ padding: "14px" }}>{r.contact_name}<br/>
                        <span style={{ color: "#8A8A8A", fontSize: 12 }}>{r.business_name}</span></td>
                      <td style={{ padding: "14px" }}>{r.email}</td>
                      <td style={{ padding: "14px" }}>{r.phone || "—"}</td>
                      <td style={{ padding: "14px" }}>{fmtDate(r.created_at)}</td>
                      <td style={{ padding: "14px" }}>
                        <span style={{ background: tone.bg, color: tone.fg,
                                       padding: "3px 10px", fontSize: 12, letterSpacing: "0.06em",
                                       textTransform: "uppercase", fontWeight: 600 }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: "14px", textAlign: "right" }}>
                        <button onClick={() => openDetail(r.id)}
                                data-testid={`admin-beta-open-${r.id}`}
                                style={{ background: "transparent", border: "1px solid #D1CEC7",
                                         padding: "6px 14px", cursor: "pointer", fontSize: 12.5 }}>
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <DetailDrawer app={selected} onClose={() => setSelected(null)} onPatch={patch}/>
      )}
    </div>
  );
}

function DetailDrawer({ app, onClose, onPatch }) {
  const [noteDraft, setNoteDraft] = useState("");
  const utm = app.utm || {};
  const tools = app.current_tools || [];
  return (
    <div data-testid="admin-beta-drawer"
         style={{ position: "fixed", inset: 0, background: "rgba(15,14,12,0.55)",
                  display: "flex", justifyContent: "flex-end", zIndex: 60 }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ width: "min(560px, 100%)", background: "white", overflowY: "auto",
                    padding: "32px 30px", fontFamily: "Manrope, system-ui, sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      marginBottom: 20 }}>
          <div>
            <div style={{ letterSpacing: "0.22em", textTransform: "uppercase",
                          color: "#A68A64", fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
              {COUNTRY_LABELS[app.country] || app.country}
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, margin: 0, fontWeight: 500 }}>
              {app.business_name}
            </h2>
          </div>
          <button onClick={onClose} data-testid="admin-beta-drawer-close"
                  style={{ background: "transparent", border: "1px solid #D1CEC7",
                           padding: "6px 12px", cursor: "pointer" }}>Close</button>
        </div>

        <Section title="Contact">
          <Row k="Name" v={app.contact_name}/>
          <Row k="Email" v={app.email}/>
          <Row k="Phone" v={app.phone}/>
          <Row k="Website / social" v={app.website}/>
          <Row k="Region" v={app.region}/>
        </Section>

        <Section title="Studio">
          <Row k="Business type" v={humanize(app.business_type)}/>
          <Row k="Annual project volume" v={volumeLabel(app.annual_project_volume)}/>
          <Row k="Current tools" v={tools.length ? tools.map(humanize).join(", ") : "—"}/>
        </Section>

        <Section title="Application">
          <Row k="Biggest frustration" v={app.biggest_frustration} multiline/>
          <Row k="How they heard about us" v={app.referral_source}/>
          <Row k="Marketing consent" v={app.marketing_consent ? "Yes" : "No"}/>
          <Row k="Applied" v={fmtDate(app.created_at)}/>
          <Row k="UTM source"    v={utm.utm_source}/>
          <Row k="UTM medium"    v={utm.utm_medium}/>
          <Row k="UTM campaign"  v={utm.utm_campaign}/>
          <Row k="Referrer"      v={utm.referrer}/>
        </Section>

        <Section title="Status">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {STATUSES.map(s => (
              <button key={s} onClick={() => onPatch(app.id, { status: s })}
                      data-testid={`admin-beta-set-${s}`}
                      style={{ background: app.status === s ? "#1C1C1C" : "transparent",
                               color: app.status === s ? "white" : "#1C1C1C",
                               border: "1px solid #D1CEC7", padding: "6px 14px",
                               cursor: "pointer", fontSize: 12.5,
                               letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {s}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#8A8A8A" }}>
            Last reviewed: {fmtDate(app.reviewed_at)} {app.reviewed_by ? ` · by ${app.reviewed_by}` : ""}
          </div>
        </Section>

        <Section title="Admin notes">
          <div style={{ marginBottom: 10 }}>
            {(app.admin_notes || []).length === 0 && (
              <div style={{ fontSize: 13, color: "#8A8A8A" }}>No notes yet.</div>
            )}
            {(app.admin_notes || []).map(n => (
              <div key={n.id} style={{ background: "#F4F2EC", padding: "10px 12px",
                                        marginBottom: 8, fontSize: 13, lineHeight: 1.55 }}>
                <div style={{ fontSize: 11, color: "#8A8A8A", marginBottom: 4 }}>
                  {n.author} · {fmtDate(n.created_at)}
                </div>
                {n.text}
              </div>
            ))}
          </div>
          <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                    data-testid="admin-beta-note-input"
                    placeholder="Add a note (visible only to admins)"
                    style={{ width: "100%", boxSizing: "border-box", minHeight: 76,
                             border: "1px solid #D1CEC7", background: "#FBFAF3",
                             padding: "10px 12px", fontSize: 13.5,
                             fontFamily: "inherit", resize: "vertical" }}/>
          <button disabled={!noteDraft.trim()}
                  data-testid="admin-beta-note-add"
                  onClick={async () => {
                    await onPatch(app.id, { add_note: noteDraft.trim() });
                    setNoteDraft("");
                  }}
                  style={{ marginTop: 8, background: "#1C1C1C", color: "white",
                           border: "none", padding: "9px 18px",
                           fontSize: 12.5, letterSpacing: "0.06em", cursor: "pointer",
                           opacity: noteDraft.trim() ? 1 : 0.5 }}>
            Add note
          </button>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 26 }}>
      <div style={{ letterSpacing: "0.18em", textTransform: "uppercase",
                    fontSize: 11, color: "#A68A64", fontWeight: 600, marginBottom: 12 }}>
        {title}
      </div>
      <div>{children}</div>
    </section>
  );
}
function Row({ k, v, multiline }) {
  return (
    <div style={{ display: multiline ? "block" : "grid",
                  gridTemplateColumns: "160px 1fr", gap: 10,
                  padding: "6px 0", fontSize: 13.5, lineHeight: 1.55,
                  borderBottom: "1px solid #F0EDE5" }}>
      <div style={{ color: "#8A8A8A", fontSize: 12,
                    textTransform: "uppercase", letterSpacing: "0.08em" }}>{k}</div>
      <div style={{ color: "#1C1C1C", whiteSpace: multiline ? "pre-wrap" : "normal" }}>
        {v || "—"}
      </div>
    </div>
  );
}
